import type { BaseQueryApi } from "@reduxjs/toolkit/query";

import * as Sentry from "@sentry/react";

import Config from "~/config";
import classifyRtkError, {
	buildGraphqlErrorBreadcrumb,
	extractStatusCode,
	type RtkErrorClassification
} from "~/helpers/rtkErrorClassification";
import { redactSentryErrorEvent } from "~/helpers/sentryRedaction";
import {
	extractCorrelationId,
	extractGraphqlErrorCode,
	isAbortError,
	isRetryableError,
	sanitizeResponseSnippet
} from "~/services/graphqlErrorHelpers";

interface SentryRequestContext {
	endpoint: string;
	method: string;
	operationType: "query" | "mutation";
	url: string;
}

interface ErrorNetworkContext {
	classification: RtkErrorClassification;
	correlationId?: string;
	graphqlCode?: string;
	responseSnippet?: string;
	retryable: boolean;
	statusCode?: number;
}

interface SettingsState {
	settings?: { tenantId?: string };
}

function extractRoute(): string {
	if (typeof window === "undefined") return "unknown";
	return window.location.pathname + (window.location.hash || "");
}

function extractTenantFromState(api: BaseQueryApi): string | undefined {
	try {
		const state = api.getState() as SettingsState | undefined;
		const tenantId = state?.settings?.tenantId;
		if (typeof tenantId === "string" && tenantId) return tenantId;
	} catch {
		// getState may throw during teardown — safe to ignore
	}
	return undefined;
}

/** Severity policy for RTK / GraphQL failures. Centralized so logger paths and
 * the RTK capture path stay in sync. 4xx client errors are usually expected
 * (validation, not-found) and are downgraded to keep the Sentry signal high. */
export function severityForClassification(classification: RtkErrorClassification): Sentry.SeverityLevel {
	return classification === "client" ? "warning" : "error";
}

/** Deterministic fingerprint for RTK failures so Sentry groups events by
 * endpoint + classification + status + graphql code rather than the
 * stack/message. Without this, transient message variations split a single
 * underlying failure across many issues. */
export function buildSentryFingerprint(input: {
	classification: RtkErrorClassification;
	endpoint: string;
	graphqlCode?: string;
	statusCode?: number;
}): string[] {
	const parts = [
		"rtk",
		input.endpoint || "unknown",
		input.classification,
		input.statusCode !== undefined ? String(input.statusCode) : "no-status",
		input.graphqlCode ?? "no-code"
	];
	return parts;
}

const UNIDENTIFIED_ERROR = "unknown";

export function buildLoggerFingerprint(input: {
	classification: RtkErrorClassification;
	graphqlCode?: string;
	identity: string;
	statusCode?: number;
}): string[] {
	return [
		"logger",
		input.identity || UNIDENTIFIED_ERROR,
		input.classification,
		input.statusCode !== undefined ? String(input.statusCode) : "no-status",
		input.graphqlCode ?? "no-code"
	];
}

export function captureRtkQueryFailure(params: {
	api: BaseQueryApi;
	durationMs: number;
	error: unknown;
	request: SentryRequestContext;
}): void {
	const { api, durationMs, error, request } = params;

	// Aborted requests (component unmount, resetApiState, navigation) are
	// expected and should never reach Sentry — drop them at the source so we
	// don't pay even the beforeSend cost.
	if (isAbortError(error)) return;

	const classification = classifyRtkError(error);
	const network: ErrorNetworkContext = {
		classification,
		correlationId: extractCorrelationId(error),
		graphqlCode: extractGraphqlErrorCode(error),
		responseSnippet: sanitizeResponseSnippet(error),
		retryable: isRetryableError(error),
		statusCode: extractStatusCode(error)
	};

	// HC0051 = HotChocolate paging cap exceeded. Leaves a trail on subsequent
	// Sentry events so paging-cap regressions are easy to spot at a glance.
	if (network.graphqlCode === "HC0051") {
		Sentry.addBreadcrumb({
			category: "graphql.paging",
			data: {
				endpoint: request.endpoint,
				operationType: request.operationType
			},
			level: "warning",
			message: "GraphQL paging cap exceeded (HC0051)"
		});
	}

	Sentry.withScope((scope) => {
		scope.setTag("rtk.endpoint", request.endpoint);
		scope.setTag("rtk.operationType", request.operationType);
		scope.setTag("http.method", request.method);
		scope.setTag("error.classification", network.classification);
		scope.setTag("error.retryable", network.retryable ? "true" : "false");
		if (network.statusCode !== undefined) {
			scope.setTag("http.status_code", String(network.statusCode));
		}
		if (network.graphqlCode) {
			scope.setTag("graphql.code", network.graphqlCode);
		}
		if (network.correlationId) {
			scope.setTag("correlation.id", network.correlationId);
		}

		const route = extractRoute();
		scope.setTag("route", route);

		const tenantId = extractTenantFromState(api);
		if (tenantId) scope.setTag("tenant.id", tenantId);

		scope.setFingerprint(
			buildSentryFingerprint({
				classification: network.classification,
				endpoint: request.endpoint,
				graphqlCode: network.graphqlCode,
				statusCode: network.statusCode
			})
		);

		scope.setContext("rtkQuery", {
			classification: network.classification,
			correlationId: network.correlationId ?? null,
			durationMs: Math.round(durationMs),
			endpoint: request.endpoint,
			environment: Config.environment,
			graphqlCode: network.graphqlCode ?? null,
			method: request.method,
			operationType: request.operationType,
			release: Config.buildVersion,
			retryable: network.retryable,
			route,
			statusCode: network.statusCode ?? null,
			tenantId: tenantId ?? null,
			url: request.url
		});

		if (network.responseSnippet) {
			scope.setExtra("responseSnippet", network.responseSnippet);
		}
		scope.setExtra("durationMs", Math.round(durationMs));

		const level = severityForClassification(network.classification);
		scope.setLevel(level);

		if (error instanceof Error) {
			Sentry.captureException(error);
		} else {
			Sentry.captureMessage(
				`RTK query failed: ${request.endpoint} (${network.statusCode ?? network.classification})`,
				level
			);
		}
	});
}

export function addGraphqlErrorCodeBreadcrumb(params: { error: unknown; request: SentryRequestContext }): void {
	const { error, request } = params;
	Sentry.addBreadcrumb(
		buildGraphqlErrorBreadcrumb({ endpoint: request.endpoint, error, operationType: request.operationType })
	);
}

export function buildGraphqlRequestContext(api: BaseQueryApi, url: string): SentryRequestContext {
	return {
		endpoint: api.endpoint || "unknown",
		method: "POST",
		operationType: api.type,
		url
	};
}

const SENSITIVE_KEYS = ["accessToken", "password", "refreshToken"];

const ABORT_NEEDLES = ["aborterror", "the operation was aborted", "the user aborted a request", "request aborted"];

const RTK_REJECTED_NOISE_ENDPOINTS = new Set([
	"GetFeatureFlag",
	"GetPortalAccount",
	"GetPortalItemsPaginated",
	"GetPortalItemPrices"
]);

function isKnownRtkRejectedNoise(event: Sentry.ErrorEvent): boolean {
	const message = typeof event.message === "string" ? event.message : "";
	const match = message.match(/^RTK query rejected:\s+([^(]+)\s+\(unknown\)$/i);
	if (!match) return false;
	const endpoint = match[1]?.trim();
	return endpoint ? RTK_REJECTED_NOISE_ENDPOINTS.has(endpoint) : false;
}

/** Centralized so beforeSend can drop abort/cancellation noise without hiding
 * real failures (which carry stacks, status codes, and don't match these strings). */
function isAbortNoiseEvent(event: Sentry.ErrorEvent): boolean {
	const exception = event.exception?.values?.[0];
	if (exception) {
		if (exception.type === "AbortError") return true;
		const value = exception.value?.toLowerCase() ?? "";
		if (value && ABORT_NEEDLES.some((needle) => value.includes(needle))) return true;
	}
	const message = typeof event.message === "string" ? event.message.toLowerCase() : "";
	if (message && ABORT_NEEDLES.some((needle) => message.includes(needle))) return true;
	return false;
}

export function sentryBeforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
	// Drop expected request-cancellation noise. These fire on navigation,
	// component unmount, and resetApiState() — they are not real failures and
	// would otherwise dominate the issues list.
	if (isAbortNoiseEvent(event)) return null;

	// Drop known high-volume RTK rejected noise where endpoint + classification are
	// unknown and no diagnostic context is attached.
	if (isKnownRtkRejectedNoise(event)) return null;

	const scrub = (obj: unknown) => {
		if (!obj || typeof obj !== "object") return;
		const record = obj as { [key: string]: unknown };
		for (const key of Object.keys(record)) {
			if (SENSITIVE_KEYS.includes(key)) {
				record[key] = "[REDACTED]";
			}
		}
	};

	if (event.extra) scrub(event.extra);

	if (event.contexts) {
		for (const ctx of Object.values(event.contexts)) {
			scrub(ctx);
		}
	}

	if (event.breadcrumbs) {
		for (const breadcrumb of event.breadcrumbs) {
			scrub(breadcrumb.data);
		}
	}

	return redactSentryErrorEvent(event);
}

export default captureRtkQueryFailure;
